// Push Notification Service for Blackified
// Handles service worker registration and push subscription management

const API = `${window.location.origin}/api`;

// VAPID public key (you'll need to generate this and add the private key to backend)
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

/**
 * Check if push notifications are supported
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default', 'granted', or 'denied'
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported');
    return 'unsupported';
  }
  
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'error';
  }
};

/**
 * Register service worker
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    console.log('Service Worker registered:', registration.scope);
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

/**
 * Get or create push subscription
 */
export const subscribeToPush = async (registration) => {
  if (!registration) {
    console.warn('No service worker registration');
    return null;
  }
  
  try {
    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      console.log('Existing push subscription found');
      return subscription;
    }
    
    // Create new subscription
    if (!VAPID_PUBLIC_KEY) {
      console.warn('VAPID public key not configured');
      return null;
    }
    
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    
    console.log('New push subscription created:', subscription);
    
    // Send subscription to backend
    await sendSubscriptionToServer(subscription);
    
    return subscription;
  } catch (error) {
    console.error('Error subscribing to push:', error);
    return null;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      
      // Notify backend
      await removeSubscriptionFromServer(subscription);
      
      console.log('Unsubscribed from push notifications');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return false;
  }
};

/**
 * Send subscription to backend server
 */
const sendSubscriptionToServer = async (subscription) => {
  try {
    const response = await fetch(`${API}/notifications/push-subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        subscription: subscription.toJSON()
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to save subscription');
    }
    
    console.log('Push subscription saved to server');
    return true;
  } catch (error) {
    console.error('Error saving subscription to server:', error);
    return false;
  }
};

/**
 * Remove subscription from backend server
 */
const removeSubscriptionFromServer = async (subscription) => {
  try {
    const response = await fetch(`${API}/notifications/push-unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to remove subscription');
    }
    
    console.log('Push subscription removed from server');
    return true;
  } catch (error) {
    console.error('Error removing subscription from server:', error);
    return false;
  }
};

/**
 * Show a local notification (for testing or fallback)
 */
export const showLocalNotification = async (title, options = {}) => {
  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    return registration.showNotification(title, {
      icon: '/logo192.png',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      ...options
    });
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
};

/**
 * Initialize push notifications
 * Call this when the app loads and user is authenticated
 */
export const initializePushNotifications = async () => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return { supported: false };
  }
  
  const permission = getNotificationPermission();
  
  if (permission === 'denied') {
    console.log('Push notifications blocked by user');
    return { supported: true, permission: 'denied' };
  }
  
  const registration = await registerServiceWorker();
  
  if (!registration) {
    return { supported: true, permission, registered: false };
  }
  
  // Only subscribe if permission is granted
  let subscription = null;
  if (permission === 'granted') {
    subscription = await subscribeToPush(registration);
  }
  
  return {
    supported: true,
    permission,
    registered: true,
    subscribed: !!subscription
  };
};

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  showLocalNotification,
  initializePushNotifications
};
